import type { ClientAuth, Profile, ServerMessage } from '@cratered/shared';
import { discord, net, profile, roles, showToast } from './store';

/**
 * Creator-side plumbing for custom character art: where the files live, uploading and removing
 * them, and keeping the roles signal in step with the server.
 */

export type UgcPart = 'body' | 'head';
export const UGC_MAX_BYTES = 300 * 1024;
export const UGC_MAX_SIDE = 512;

function apiBase(): string {
  return discord.value?.apiBase ?? '';
}

/** Full URL of a served custom part, from the path stored in the profile. */
export function ugcUrl(path: string): string {
  return `${apiBase()}/api/ugc/${path}`;
}

export function onRolesMessage(msg: ServerMessage): void {
  if (msg.t === 'roles') roles.value = { admin: msg.mine.admin, creator: msg.mine.creator, creators: msg.creators };
}

/** The same credentials the socket said hello with; uploads go over HTTP and must prove who they are too. */
function auth(): ClientAuth | null {
  return net.value?.auth ?? null;
}

async function post(path: string, body: Record<string, unknown>): Promise<{ profile?: Profile; error?: string }> {
  const a = auth();
  if (!a) return { error: 'Not signed in yet.' };
  const res = await fetch(`${apiBase()}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...body, auth: a }),
  });
  const data = (await res.json().catch(() => ({}))) as { profile?: Profile; error?: string };
  if (!res.ok) return { error: data.error ?? `Upload failed (${res.status})` };
  return data;
}

function toBase64(buf: ArrayBuffer): string {
  let s = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

export async function uploadCustomPart(part: UgcPart, file: File): Promise<boolean> {
  if (file.size > UGC_MAX_BYTES) {
    showToast(`That PNG is too big: ${Math.round(UGC_MAX_BYTES / 1024)} KB at most.`, 'error');
    return false;
  }
  const res = await post('/api/ugc', { part, png: toBase64(await file.arrayBuffer()) });
  if (res.error || !res.profile) {
    showToast(res.error ?? 'Upload failed.', 'error');
    return false;
  }
  profile.value = res.profile;
  showToast(part === 'body' ? 'Custom body uploaded.' : 'Custom head uploaded.');
  return true;
}

export async function removeCustomPart(part: UgcPart): Promise<void> {
  const res = await post('/api/ugc/remove', { part });
  if (res.error || !res.profile) {
    showToast(res.error ?? 'Could not remove it.', 'error');
    return;
  }
  profile.value = res.profile;
}
